const API_BASE = '/api/v1/documents';

export interface Document {
  id: number;
  originalFilename: string;
  fileType: string;
  mimeType: string | null;
  fileSize: number;
  parsed: boolean;
  verified: boolean;
  parsingConfidence: number | null;
  documentDate: string | null;
  labName: string | null;
  pageCount: number | null;
  tags: string | null;
  notes: string | null;
  uploadedAt: string;
}

export interface DocumentDetail extends Document {
  filename: string;
  storagePath: string;
  parsedData: unknown;
  parsingErrors: string[] | null;
  labParameters: LabParameter[];
}

export interface LabParameter {
  id: number;
  parameterCode: string;
  parameterName: string;
  value: number;
  unit: string;
  referenceMin: number | null;
  referenceMax: number | null;
  referenceRange: string | null;
  isWithinRange: boolean;
  section: string | null;
  testDate: string;
  labName: string | null;
}

export interface ParsedParameter {
  parameterName: string;
  parameterCode?: string;
  value: number;
  unit: string;
  referenceMin?: number;
  referenceMax?: number;
  isWithinRange?: boolean;
  confidence: number;
  section?: string;
}

export interface ParseResult {
  documentDate: string | null;
  labName: string | null;
  parametersFound: number;
  parameters: ParsedParameter[];
  confidence: number;
  warnings: string[];
  errors: string[];
}

export interface DocumentStats {
  totalDocuments: number;
  parsedDocuments: number;
  verifiedDocuments: number;
  totalParameters: number;
}

export interface DocumentsResponse {
  success: boolean;
  documents: Document[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export async function getDocuments(options?: {
  page?: number;
  limit?: number;
  parsed?: boolean;
  verified?: boolean;
}): Promise<DocumentsResponse> {
  const params = new URLSearchParams();
  if (options?.page) params.set('page', options.page.toString());
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.parsed !== undefined) params.set('parsed', options.parsed.toString());
  if (options?.verified !== undefined) params.set('verified', options.verified.toString());

  const response = await fetch(`${API_BASE}?${params}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch documents');
  }

  return response.json();
}

export async function getDocument(id: number): Promise<{ success: boolean; document: DocumentDetail }> {
  const response = await fetch(`${API_BASE}/${id}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch document');
  }

  return response.json();
}

export async function uploadDocument(file: File): Promise<{ success: boolean; document: Document; message: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE}/upload`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upload document');
  }

  return response.json();
}

export async function parseDocument(id: number): Promise<{ success: boolean; result: ParseResult; message: string }> {
  const response = await fetch(`${API_BASE}/${id}/parse`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to parse document');
  }

  return response.json();
}

export async function verifyDocument(
  id: number,
  verified: boolean,
  notes?: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE}/${id}/verify`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ verified, notes }),
  });

  if (!response.ok) {
    throw new Error('Failed to verify document');
  }

  return response.json();
}

export async function downloadDocument(id: number, filename: string): Promise<void> {
  const response = await fetch(`${API_BASE}/${id}/download`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to download document');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export async function deleteDocument(id: number): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to delete document');
  }

  return response.json();
}

export async function getDocumentStats(): Promise<{ success: boolean; stats: DocumentStats }> {
  const response = await fetch(`${API_BASE}/stats`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch document stats');
  }

  return response.json();
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('it-IT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}
